import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { thumbUrl } from "@/lib/api";

const MAX_ZOOM = 4;
const DBL_ZOOM = 2.5;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Full-screen premium image lightbox: keyboard + arrow + swipe nav, double-tap /
// pinch zoom with pan, blurred backdrop, adjacent-image preloading and a discreet
// position counter. All navigation happens inside the same component (no reload).
export default function Lightbox({ images = [], index = 0, setIndex, open, onClose, alt = "" }) {
  const n = images.length;
  const go = useCallback((dir) => setIndex((i) => (i + dir + n) % n), [n, setIndex]);
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const gesture = useRef({});
  const lastTap = useRef(0);
  const zoomed = zoom.scale > 1.01;

  const resetZoom = useCallback(() => setZoom({ scale: 1, x: 0, y: 0 }), []);
  // Reset the zoom whenever the image or open-state changes.
  useEffect(() => { resetZoom(); }, [index, open, resetZoom]);

  // Keyboard navigation + scroll lock.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") { resetZoom(); go(-1); }
      else if (e.key === "ArrowRight") { resetZoom(); go(1); }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, go, onClose, resetZoom]);

  // Preload adjacent images so switching is instant (no white screen).
  useEffect(() => {
    if (!open || n < 2) return;
    [index + 1, index - 1, index + 2].forEach((k) => {
      const i = ((k % n) + n) % n;
      const im = new Image();
      im.src = thumbUrl(images[i], 1600, 92);
    });
  }, [open, index, n, images]);

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const toggleZoom = () => setZoom((z) => (z.scale > 1.01 ? { scale: 1, x: 0, y: 0 } : { scale: DBL_ZOOM, x: 0, y: 0 }));

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      gesture.current = { mode: "pinch", startDist: dist(e.touches), startScale: zoom.scale };
      return;
    }
    const now = Date.now();
    if (now - lastTap.current < 300) {
      toggleZoom();
      lastTap.current = 0;
      gesture.current = {};
      return;
    }
    lastTap.current = now;
    gesture.current = {
      mode: zoomed ? "pan" : "swipe",
      x: e.touches[0].clientX, y: e.touches[0].clientY,
      ox: zoom.x, oy: zoom.y, t: now,
    };
  };

  const onTouchMove = (e) => {
    const g = gesture.current;
    if (g.mode === "pinch" && e.touches.length === 2) {
      const s = clamp((g.startScale || 1) * (dist(e.touches) / (g.startDist || 1)), 1, MAX_ZOOM);
      setZoom((z) => ({ ...z, scale: s }));
    } else if (g.mode === "pan" && e.touches.length === 1) {
      const dx = e.touches[0].clientX - g.x;
      const dy = e.touches[0].clientY - g.y;
      setZoom((z) => ({ ...z, x: g.ox + dx, y: g.oy + dy }));
    }
  };

  const onTouchEnd = (e) => {
    const g = gesture.current;
    gesture.current = {};
    if (g.mode === "pinch") {
      if (zoom.scale <= 1.05) resetZoom();
      return;
    }
    if (g.mode === "swipe" && n > 1) {
      const t1 = e.changedTouches[0];
      const dx = t1.clientX - g.x;
      const dy = t1.clientY - g.y;
      const dt = Date.now() - (g.t || 0);
      if (dt < 700 && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        resetZoom();
        go(dx < 0 ? 1 : -1);
      }
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex flex-col bg-black/90"
        >
          {/* Blurred backdrop of the current photo — isolates the vehicle. */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={`bg-${index}`}
                src={thumbUrl(images[index], 900, 40)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.35 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full w-full scale-125 object-cover blur-3xl"
                draggable={false}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-black/40" style={{ WebkitBackdropFilter: "blur(18px)", backdropFilter: "blur(18px)" }} />
          </div>

          {/* top bar */}
          <div className="relative flex items-center justify-between px-5 py-4 text-white" onClick={(e) => e.stopPropagation()}>
            <span data-testid="lightbox-counter" className="rounded-full bg-white/10 px-3.5 py-1.5 text-sm font-semibold tracking-wide backdrop-blur">
              {index + 1} / {n}
            </span>
            <button
              data-testid="lightbox-close"
              onClick={onClose}
              aria-label="Close"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
            >
              <X size={22} />
            </button>
          </div>

          {/* main stage */}
          <div
            className="relative flex flex-1 items-center justify-center overflow-hidden px-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ touchAction: "none" }}
          >
            {n > 1 && !zoomed && (
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); resetZoom(); go(-1); }}
                aria-label="Previous"
                className="absolute left-3 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/25 sm:left-6"
              >
                <ChevronLeft size={26} />
              </button>
            )}

            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={index}
                src={thumbUrl(images[index], 1600, 92)}
                alt={alt}
                data-testid="lightbox-image"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => { e.stopPropagation(); toggleZoom(); }}
                className="relative z-10 max-h-[76vh] max-w-full select-none rounded-xl object-contain shadow-2xl"
                style={{
                  transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
                  transition: gesture.current.mode === "pinch" || gesture.current.mode === "pan" ? "none" : "transform 0.2s ease-out",
                  cursor: zoomed ? "grab" : "zoom-in",
                }}
                draggable={false}
              />
            </AnimatePresence>

            {n > 1 && !zoomed && (
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); resetZoom(); go(1); }}
                aria-label="Next"
                className="absolute right-3 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/25 sm:right-6"
              >
                <ChevronRight size={26} />
              </button>
            )}

            {/* discreet position indicator at the bottom of the image */}
            {n > 1 && (
              <div
                data-testid="lightbox-counter-bottom"
                className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-xs font-medium tracking-wide text-white/90 backdrop-blur"
              >
                {index + 1} / {n}
              </div>
            )}
          </div>

          {/* thumbnails */}
          {n > 1 && (
            <div className="relative flex justify-center gap-2 overflow-x-auto px-4 py-5 no-scrollbar" onClick={(e) => e.stopPropagation()}>
              {images.map((im, i) => (
                <button
                  key={i}
                  data-testid={`lightbox-thumb-${i}`}
                  onClick={() => { resetZoom(); setIndex(i); }}
                  className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                    i === index ? "border-primary opacity-100" : "border-transparent opacity-50 hover:opacity-90"
                  }`}
                >
                  <img src={thumbUrl(im, 160)} alt="" loading="lazy" className="h-full w-full object-cover" draggable={false} />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export { ZoomIn };
